import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import OnboardingGate from "@/components/auth/OnboardingGate";
import MentorCard from "@/components/mentors/MentorCard";
import { useProfile } from "@/lib/profile/useProfile";
import { useMentors } from "@/lib/mentors/useMentors";
import { useMentorRequests } from "@/lib/mentors/useMentorRequests";
import { getApp } from "@/lib/apps";
import { CardGridSkeleton } from "@/components/common/Skeletons";
import EmptyState from "@/components/common/EmptyState";
import { GraduationCap } from "lucide-react";

const MentorsInner = () => {
  const { profile, loading: pLoading } = useProfile();
  const app = profile?.active_app ?? null;
  const meta = useMemo(() => (app ? getApp(app) : null), [app]);

  const [specialty, setSpecialty] = useState<string | null>(null);
  const { mentors, loading } = useMentors({ app, specialty });
  const { requestMentor } = useMentorRequests();
  const [busyId, setBusyId] = useState<string | null>(null);

  const allSpecialties = useMemo(() => {
    const s = new Set<string>();
    for (const m of mentors) (m.specialties ?? []).forEach((x) => s.add(x));
    return Array.from(s).sort();
  }, [mentors]);

  if (pLoading) {
    return (
      <main className="mx-auto max-w-6xl px-6 py-10">
        <CardGridSkeleton cards={6} cols={3} />
      </main>
    );
  }

  if (!app) {
    return (
      <main className="mx-auto max-w-2xl px-6 py-12">
        <h1 className="text-2xl font-semibold">Pick a community first</h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Visit your account to choose your active app, then come back to browse mentors.
        </p>
        <Link
          to="/account"
          className="mt-6 inline-flex rounded-full bg-foreground px-5 py-2 text-sm font-medium text-background"
        >
          Go to Account
        </Link>
      </main>
    );
  }

  return (
    <main className={`mx-auto max-w-6xl px-6 py-10 ${meta?.themeClass ?? ""}`}>
      <header className="mb-8 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            {meta?.community} · Mentors
          </p>
          <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tight">
            Find a mentor in {meta?.name}
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
            Verified, community-grounded mentors. Send a short note about what you're working on
            — they decide whether to accept.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/mentor-sessions"
            className="rounded-full border border-border px-4 py-2 text-xs font-medium hover:bg-muted transition-colors"
          >
            My sessions
          </Link>
          <Link
            to="/mentor-apply"
            className="rounded-full bg-foreground text-background px-4 py-2 text-xs font-medium hover:opacity-90 transition-opacity"
          >
            Become a mentor
          </Link>
        </div>
      </header>

      {allSpecialties.length > 0 ? (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setSpecialty(null)}
            className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
              specialty === null
                ? "bg-foreground text-background border-foreground"
                : "border-border text-muted-foreground hover:bg-muted"
            }`}
          >
            All
          </button>
          {allSpecialties.map((s) => (
            <button
              type="button"
              key={s}
              onClick={() => setSpecialty(s)}
              className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                specialty === s
                  ? "bg-foreground text-background border-foreground"
                  : "border-border text-muted-foreground hover:bg-muted"
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      ) : null}

      {loading ? (
        <CardGridSkeleton cards={6} cols={3} />
      ) : mentors.length === 0 ? (
        <EmptyState
          icon={GraduationCap}
          title={`No mentors in ${meta?.name} yet`}
          description="Be the first — apply to mentor and help open the door for everyone after you."
          ctaLabel="Become a mentor"
          ctaHref="/mentor-apply"
          secondaryLabel="Back to Discover"
          secondaryHref="/discover"
        />
      ) : (
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {mentors.map((m) => (
            <MentorCard
              key={`${m.user_id}-${m.app}`}
              mentor={m}
              busy={busyId === m.user_id}
              onRequest={async (message) => {
                setBusyId(m.user_id);
                const res = await requestMentor(m.user_id, m.app as string, message);
                setBusyId(null);
                return res;
              }}
            />
          ))}
        </div>
      )}
    </main>
  );
};

const Mentors = () => (
  <div className="min-h-screen flex flex-col bg-background text-foreground">
    <SiteHeader />
    <OnboardingGate>
      <MentorsInner />
    </OnboardingGate>
    <SiteFooter />
  </div>
);

export default Mentors;
